import { useMemo, useState, type ReactNode } from 'react';
import {
  Box, Typography, Stack, TextField, InputAdornment, Chip, Button, MenuItem,
  Collapse, alpha, useTheme, Tabs, Tab, Grid,
} from '@mui/material';
import { Search, Add, FilterList, ClearAll, FileDownload } from '@mui/icons-material';
import { PARTNER_SERVICE_CONFIG, PARTNER_STATUS_OPTIONS, SERVICE_HUB_OPTIONS, getPartnerCreateLabel } from '../../constants/partnerServices';

interface PartnerOpsBarProps {
  entityTab: number;
  onEntityTabChange: (index: number) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  search: string;
  onSearchChange: (q: string) => void;
  zoneFilter: string;
  onZoneFilterChange: (zoneId: string) => void;
  hubFilter: string;
  onHubFilterChange: (hub: string) => void;
  fromDate: string;
  onFromDateChange: (d: string) => void;
  toDate: string;
  onToDateChange: (d: string) => void;
  zones: { _id: string; name: string }[];
  totalCount: number;
  onAdd: () => void;
  onClearFilters: () => void;
  title?: string;
  hideEntityTabs?: boolean;
  lockHubFilter?: boolean;
  onExport?: () => void;
  exporting?: boolean;
  /** Optional header action (e.g. setup notes help icon). */
  titleAction?: ReactNode;
}

export default function PartnerOpsBar({
  entityTab,
  onEntityTabChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  zoneFilter,
  onZoneFilterChange,
  hubFilter,
  onHubFilterChange,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  zones,
  totalCount,
  onAdd,
  onClearFilters,
  title,
  hideEntityTabs,
  lockHubFilter,
  onExport,
  exporting,
  titleAction,
}: PartnerOpsBarProps) {
  const theme = useTheme();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const serviceLabel = title || PARTNER_SERVICE_CONFIG[entityTab]?.label || 'Partners';
  const addPartnerLabel = hideEntityTabs
    ? 'Add Partner'
    : `Add ${getPartnerCreateLabel(PARTNER_SERVICE_CONFIG[entityTab]?.key || 'driver')}`;

  const extraFilterCount = useMemo(() => {
    let n = 0;
    if (fromDate) n += 1;
    if (toDate) n += 1;
    if (zoneFilter) n += 1;
    if (hubFilter && !lockHubFilter) n += 1;
    return n;
  }, [fromDate, toDate, zoneFilter, hubFilter, lockHubFilter]);

  const fieldSx = {
    '& .MuiInputBase-root': { bgcolor: 'background.paper' },
  };

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2.5,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="subtitle1" fontWeight={800}>{serviceLabel}</Typography>
            {titleAction}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {Number(totalCount) === 1 ? '1 partner' : `${Number(totalCount) || 0} partners`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {onExport && (
            <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={onExport} disabled={exporting || totalCount === 0}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export</Box>
            </Button>
          )}
          <Button
            size="small"
            variant={filtersOpen || extraFilterCount > 0 ? 'contained' : 'outlined'}
            color={filtersOpen || extraFilterCount > 0 ? 'primary' : 'inherit'}
            startIcon={<FilterList />}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            Filters{extraFilterCount > 0 ? ` (${extraFilterCount})` : ''}
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={onAdd}>
            {addPartnerLabel}
          </Button>
        </Stack>
      </Stack>

      {!hideEntityTabs && (
        <Tabs
          value={entityTab}
          onChange={(_, v) => onEntityTabChange(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            minHeight: 42,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' },
          }}
        >
          {PARTNER_SERVICE_CONFIG.map((c) => (
            <Tab key={c.key} label={c.label.replace(' Onboarding', '')} />
          ))}
        </Tabs>
      )}

      <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search name, email, or phone…"
          value={search}
          autoComplete="off"
          name="partner-list-search"
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: '1 1 240px', maxWidth: 420, minWidth: 200 }}
        />
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: '1 1 280px' }}>
          {PARTNER_STATUS_OPTIONS.map((opt) => (
            <Chip
              key={opt.value || 'all'}
              label={opt.label}
              size="small"
              onClick={() => onStatusFilterChange(opt.value)}
              color={statusFilter === opt.value ? (opt.color || 'primary') : 'default'}
              variant={statusFilter === opt.value ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Box>

      <Collapse in={filtersOpen} unmountOnExit>
        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={800}>Advanced filters</Typography>
            {extraFilterCount > 0 && (
              <Button size="small" variant="text" startIcon={<ClearAll />} onClick={onClearFilters}>
                Clear filters
              </Button>
            )}
          </Stack>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From date"
                value={fromDate}
                onChange={(e) => onFromDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To date"
                value={toDate}
                onChange={(e) => onToDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Zone"
                value={zoneFilter}
                onChange={(e) => onZoneFilterChange(e.target.value)}
                sx={fieldSx}
              >
                <MenuItem value="">All zones</MenuItem>
                {zones.map((z) => <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>)}
              </TextField>
            </Grid>
            {!lockHubFilter && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Service hub"
                  value={hubFilter}
                  onChange={(e) => onHubFilterChange(e.target.value)}
                  sx={fieldSx}
                >
                  <MenuItem value="">All hubs</MenuItem>
                  {SERVICE_HUB_OPTIONS.map((h) => <MenuItem key={h.value} value={h.value}>{h.label}</MenuItem>)}
                </TextField>
              </Grid>
            )}
          </Grid>
          {extraFilterCount === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
              {lockHubFilter
                ? 'Narrow the list by date range or zone. Status chips and search stay visible above.'
                : 'Narrow the list by date range, zone, or service hub. Status chips and search stay visible above.'}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
